/**
 * CryptoPayModal — USDT (TRC-20) payment collector.
 *
 * • Prominent "USDT · TRC-20 network only" warning banner
 * • Scannable QR + one-click wallet copy with toast confirmation
 * • Live status polling after submission: Awaiting → Processing → Success
 * • Every async path is guarded — no unhandled promise rejections
 * • Copy uses clipboard API with a graceful text-select fallback
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import {
  Copy, CheckCircle2, Loader2, X, ExternalLink, AlertTriangle, ShieldAlert,
} from "lucide-react";
import ErrorBoundary from "./ErrorBoundary";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 6000;
const POLL_MAX = 40;      // ~4 min total polling window
const REQUEST_TIMEOUT = 15000;

function CryptoPayInner({
  open, onClose, lookupKey, targetAmount, topupCode, currentTier,
  referralCode: initialReferral, onSubmitted,
}) {
  const [info, setInfo] = useState(null);
  const [email, setEmail] = useState("");
  const [txid, setTxid] = useState("");
  const [sender, setSender] = useState("");
  const [referralCode, setReferralCode] = useState(initialReferral || "");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyErr, setCopyErr] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(null);   // initial submit response
  const [statusDoc, setStatusDoc] = useState(null);   // latest poll response
  const [pollCount, setPollCount] = useState(0);
  const [pollTimeout, setPollTimeout] = useState(false);
  const pollRef = useRef(null);
  const mountedRef = useRef(true);

  // Reset when modal opens
  useEffect(() => {
    if (!open) return;
    setError(null); setSubmitted(null); setStatusDoc(null);
    setTxid(""); setCopied(false); setCopyErr(null);
    setReferralCode(initialReferral || "");
    setPollCount(0); setPollTimeout(false);
    axios
      .get(`${API}/payments/crypto/info`, { timeout: REQUEST_TIMEOUT })
      .then((r) => { if (mountedRef.current) setInfo(r.data); })
      .catch(() => { /* handled by outer warning */ });
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [open, initialReferral]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const payAmount = useMemo(() => {
    const t = Number(targetAmount || 0);
    if (topupCode && currentTier != null) return Math.max(0, t - Number(currentTier || 0));
    return t;
  }, [targetAmount, topupCode, currentTier]);

  const wallet = info?.wallet || "";
  const network = info?.network || "TRC-20 · USDT";
  const qrValue = wallet ? `tron:${wallet}?amount=${payAmount || ""}&token=USDT` : "";

  const copyWallet = useCallback(async () => {
    if (!wallet) return;
    setCopyErr(null);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(wallet);
      } else {
        // graceful fallback for old browsers / insecure contexts
        const el = document.createElement("textarea");
        el.value = wallet;
        el.style.position = "fixed"; el.style.opacity = "0";
        document.body.appendChild(el); el.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(el);
        if (!ok) throw new Error("Copy blocked by browser");
      }
      setCopied(true);
      setTimeout(() => mountedRef.current && setCopied(false), 1600);
    } catch (e) {
      setCopyErr("Copy blocked — long-press or double-tap the address to copy manually.");
    }
  }, [wallet]);

  const startPolling = useCallback((submissionId) => {
    if (!submissionId) return;
    if (pollRef.current) clearInterval(pollRef.current);
    let ticks = 0;
    pollRef.current = setInterval(async () => {
      ticks += 1;
      if (!mountedRef.current) return;
      setPollCount(ticks);
      try {
        const { data } = await axios.get(
          `${API}/payments/crypto/status/${submissionId}`,
          { timeout: REQUEST_TIMEOUT },
        );
        if (!mountedRef.current) return;
        setStatusDoc(data);
        if (data.status === "credited" || data.status === "rejected") {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch { /* network hiccup — keep polling */ }
      if (ticks >= POLL_MAX) {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = null;
        setPollTimeout(true);
      }
    }, POLL_MS);
  }, []);

  const submit = async () => {
    if (busy) return;
    const ref = (referralCode || "").trim().toUpperCase();
    if (!ref) {
      setError("A referral code is required. Ask your inviter for their INV-XXXXXX code.");
      return;
    }
    if (!/^INV-[A-Z0-9]{4,}$/.test(ref)) {
      setError("Referral codes look like INV-XXXXXX. Please check and try again.");
      return;
    }
    setBusy(true); setError(null);
    try {
      const { data } = await axios.post(
        `${API}/payments/crypto/submit`,
        {
          lookup_key: lookupKey,
          email: email.trim(),
          txid: txid.trim(),
          sender_wallet: sender.trim() || null,
          referral_code: ref,
          topup_access_code: topupCode || null,
        },
        { timeout: REQUEST_TIMEOUT },
      );
      if (!mountedRef.current) return;
      setSubmitted(data);
      setStatusDoc(data);   // seed status from initial response
      if (data.status !== "credited") startPolling(data.submission_id);
      if (onSubmitted) { try { onSubmitted(data); } catch { /* isolate */ } }
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === "string" ? d : (e?.message || "Submission failed. Please try again."));
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  };

  const retryPoll = () => {
    setPollTimeout(false); setPollCount(0);
    if (submitted?.submission_id) startPolling(submitted.submission_id);
  };

  if (!open) return null;

  const status = statusDoc?.status || (submitted ? "pending" : null);
  const credited = status === "credited";
  const rejected = status === "rejected";
  const stateLabel =
    credited ? "Success · Terminal credited"
    : rejected ? "Rejected"
    : pollTimeout ? "Still processing — check email"
    : status === "pending" && statusDoc?.reason && statusDoc.reason !== "" ? "Processing confirmation"
    : "Awaiting deposit";

  return (
    <div
      data-testid="crypto-pay-modal"
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/85 p-4"
      onClick={onClose}
    >
      <div
        className="relative max-h-[95vh] w-full max-w-2xl overflow-y-auto border border-gold/40 bg-ink"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="crypto-pay-close"
          onClick={onClose}
          className="absolute right-4 top-4 text-white/50 hover:text-white"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="border-b border-white/10 px-8 py-6">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
            {topupCode ? "Upgrade tier · USDT payment" : "Terminal access · USDT payment"}
          </p>
          <p className="mt-2 font-serif text-3xl font-light text-white">
            Pay <span className="text-gold">${payAmount.toLocaleString()}</span>
            <span className="text-sm text-white/50"> in USDT</span>
          </p>
          <p className="mt-1 text-[11px] text-white/45">
            {topupCode
              ? `Top-up from $${Number(currentTier).toLocaleString()} → $${Number(targetAmount).toLocaleString()} · adds to seed capital`
              : `Full access — $${Number(targetAmount).toLocaleString()} credited on confirmation`}
          </p>
        </div>

        {/* PROMINENT WARNING BANNER */}
        <div
          data-testid="crypto-network-warning"
          className="mx-8 mt-4 flex items-start gap-3 rounded-md border border-[#FF9500]/60 bg-[#FF9500]/[0.10] px-4 py-3"
        >
          <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-[#FF9500]" />
          <div className="text-[12px] leading-relaxed">
            <p className="font-bold uppercase tracking-[0.15em] text-[#FF9500]">
              USDT · TRC-20 network only
            </p>
            <p className="mt-1 text-white/80">
              Send USDT on the <span className="font-semibold">Tron (TRC-20)</span> network.
              Sending on ERC-20, BEP-20 or any other chain will result in <span className="font-semibold text-[#FF003C]">permanent loss</span> of funds.
            </p>
          </div>
        </div>

        {submitted ? (
          <div className="px-8 py-8 text-center">
            {credited ? (
              <CheckCircle2 className="mx-auto h-12 w-12 text-[#00FF41]" />
            ) : rejected ? (
              <AlertTriangle className="mx-auto h-12 w-12 text-[#FF003C]" />
            ) : (
              <Loader2 className="mx-auto h-12 w-12 animate-spin text-gold" />
            )}
            <p className="mt-4 font-serif text-2xl text-white" data-testid="crypto-status-label">
              {stateLabel}
            </p>
            {credited && statusDoc?.access_code ? (
              <>
                <p className="mt-2 text-sm text-white/70">Your terminal access code is</p>
                <p
                  data-testid="crypto-status-code"
                  className="mt-2 font-tabular text-2xl tracking-[0.15em] text-gold"
                >
                  {statusDoc.access_code}
                </p>
                <p className="mt-2 max-w-md text-[11px] leading-relaxed text-white/50 mx-auto">
                  Save this code. We&apos;ve also emailed it to you along with your welcome brief.
                </p>
              </>
            ) : rejected ? (
              <p className="mt-2 max-w-md text-sm leading-relaxed text-white/60 mx-auto">
                We couldn&apos;t verify this transfer. If you believe this is a mistake, email
                us at contact@aureontrader.com with your transaction hash.
              </p>
            ) : pollTimeout ? (
              <div>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-white/60 mx-auto">
                  Tron can occasionally take longer to confirm. We&apos;ll continue verifying
                  in the background and email you the moment it lands.
                </p>
                <button
                  data-testid="crypto-poll-retry"
                  onClick={retryPoll}
                  className="mt-4 rounded-full border border-gold/50 px-6 py-2 text-[10px] uppercase tracking-[0.2em] text-gold hover:bg-gold hover:text-ink"
                >
                  Check again
                </button>
              </div>
            ) : (
              <>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-white/60 mx-auto">
                  {statusDoc?.reason || "Verifying your transfer on Tronscan…"}
                </p>
                <p className="mt-3 text-[10px] uppercase tracking-[0.2em] text-white/35">
                  Check {pollCount} · auto-refresh every 6 s
                </p>
              </>
            )}
            <p className="mt-4 font-tabular text-[11px] tracking-[0.15em] text-white/40">
              Submission · {submitted.submission_id}
            </p>
            <button
              data-testid="crypto-pay-done"
              onClick={onClose}
              className="mt-6 rounded-full border border-gold/50 px-8 py-3 text-xs uppercase tracking-[0.2em] text-gold hover:bg-gold hover:text-ink"
            >
              Close
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 px-8 py-6 md:grid-cols-[220px_1fr]">
            <div className="flex flex-col items-center">
              <div className="rounded-md bg-white p-3">
                {qrValue ? <QRCodeSVG value={qrValue} size={180} level="M" /> : (
                  <div className="h-[180px] w-[180px] animate-pulse bg-white/10" />
                )}
              </div>
              <p className="mt-3 text-[10px] uppercase tracking-[0.2em] text-white/40">{network}</p>
              {wallet && (
                <a
                  href={`https://tronscan.org/#/address/${wallet}`}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 inline-flex items-center gap-1 text-[10px] text-white/40 hover:text-gold"
                >
                  View on Tronscan <ExternalLink className="h-2.5 w-2.5" />
                </a>
              )}
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/40">
                Send to (TRC-20 USDT)
              </label>
              <div className="mt-1.5 flex items-center gap-2 border border-white/15 bg-black/30 px-3 py-2.5">
                <span
                  data-testid="crypto-wallet-address"
                  className="flex-1 truncate font-tabular text-xs text-white"
                >
                  {wallet || "Loading…"}
                </span>
                <button
                  data-testid="crypto-copy-wallet"
                  onClick={copyWallet}
                  disabled={!wallet}
                  className="flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-gold hover:text-white disabled:opacity-40"
                >
                  {copied ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
              {copied && (
                <p data-testid="crypto-copy-toast" className="mt-1 text-[10px] text-[#00FF41]">
                  ✓ Wallet address copied to clipboard
                </p>
              )}
              {copyErr && (
                <p data-testid="crypto-copy-err" className="mt-1 text-[10px] text-[#FF003C]">
                  {copyErr}
                </p>
              )}
              <p className="mt-2 text-[10px] leading-relaxed text-white/40">
                Send exactly <span className="text-gold">${payAmount.toLocaleString()} USDT</span> on the
                TRC-20 (Tron) network. Once sent, paste your transaction hash below.
                Payouts will return to your saved TRC-20 wallet.
              </p>

              <div className="mt-5 space-y-3">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-white/40">
                    Your email (for access code delivery)
                  </label>
                  <input
                    data-testid="crypto-email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@domain.com"
                    className="mt-1.5 w-full border border-white/15 bg-ink px-3 py-2.5 font-tabular text-sm text-white placeholder:text-white/25 focus:border-gold focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-[#FF9500]">
                    Referral code · required
                    <span className="ml-1 text-white/40 normal-case tracking-normal">
                      (ask your inviter — INV-XXXXXX)
                    </span>
                  </label>
                  <input
                    data-testid="crypto-referral-input"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                    placeholder="INV-XXXXXX"
                    className="mt-1.5 w-full border border-[#FF9500]/40 bg-ink px-3 py-2.5 font-tabular text-sm tracking-[0.1em] text-white placeholder:text-white/25 focus:border-gold focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-white/40">
                    TRC-20 transaction hash <span className="normal-case tracking-normal text-white/40">(optional — paste after you send)</span>
                  </label>
                  <input
                    data-testid="crypto-txid-input"
                    value={txid}
                    onChange={(e) => setTxid(e.target.value)}
                    placeholder="Paste the 64-character transaction ID (or leave blank)"
                    className="mt-1.5 w-full border border-white/15 bg-ink px-3 py-2.5 font-tabular text-xs tracking-[0.05em] text-white placeholder:text-white/25 focus:border-gold focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-white/40">
                    Your sending wallet (optional)
                  </label>
                  <input
                    data-testid="crypto-sender-input"
                    value={sender}
                    onChange={(e) => setSender(e.target.value)}
                    placeholder="T…"
                    className="mt-1.5 w-full border border-white/15 bg-ink px-3 py-2.5 font-tabular text-xs text-white placeholder:text-white/25 focus:border-gold focus:outline-none"
                  />
                </div>
              </div>

              {error && (
                <p data-testid="crypto-submit-error" className="mt-3 text-xs text-[#FF003C]">
                  {error}
                </p>
              )}

              <button
                data-testid="crypto-pay-submit"
                onClick={submit}
                disabled={busy || !email.trim() || (txid.trim() && txid.trim().length < 40) || !wallet || !referralCode.trim()}
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 text-xs uppercase tracking-[0.2em] text-ink transition-colors hover:bg-gold-light disabled:opacity-50"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit for verification"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CryptoPayModal(props) {
  if (!props.open) return null;
  return (
    <ErrorBoundary>
      <CryptoPayInner {...props} />
    </ErrorBoundary>
  );
}
